import { useInfiniteQuery } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'

import { fetchPractitioners } from 'api/fetchers/marketing-capabilities/practitioners/fetchPractitioners'
import { communitySortOptions, SortValue } from 'pages/exploreLearnings/constants/sortOptions.constants'
import { useExploreLearnings } from 'store/ExploreLearnings'

import { usePractitionersOus } from './usePractitionersSkills'
import { useSkills } from '../skills/useSkills'

const pageResolver = ({ data }: any) => {
  return data.meta.page !== data.meta.totalPages ? data.meta.page + 1 : undefined
}

export const usePractitioners = () => {
  const [searchParams] = useSearchParams()
  const search = useExploreLearnings(state => state.search)
  const ouId = useExploreLearnings(state => state.OU)
  const location = useExploreLearnings(state => state.location)
  const skills = useExploreLearnings(state => state.skill)
  const sort = useExploreLearnings(state => state.sort)

  const urlSkillsParam = searchParams.get('skills')
  const { data: skillList } = useSkills(!!urlSkillsParam)

  const validCommunitySorts: string[] = communitySortOptions.map(o => o.value)
  let effectiveSort = sort
  if (!validCommunitySorts.includes(sort)) {
    effectiveSort = SortValue.SameOUFirst
    useExploreLearnings.setState({ sort: effectiveSort })
  }

  // only send OUs that are valid for the community tab
  const { data: ouList } = usePractitionersOus()
  const validOuIds = ouList ? ouList.map((ou: any) => ou.id) : []
  const effectiveOus = ouId.filter(id => validOuIds.includes(id))

  // Handle URL skills param - map names to IDs
  // Only use store skills if no URL param is present (URL params take precedence)
  let effectiveSkills = urlSkillsParam ? [] : skills
  if (urlSkillsParam && skillList) {
    const skillLabels = urlSkillsParam.split(',').map(s => s.trim())
    const skillListData = Array.isArray(skillList) ? skillList : skillList?.data
    const validSkills = skillLabels
      .map(label => skillListData.find((skill: any) => skill.name === label))
      .filter((skill: any): skill is any => !!skill)
    effectiveSkills = validSkills.map((skill: any) => skill.id)
  }

  const params = {
    pageSize: 18,
    ...(search !== '' && { searchTerm: search }),
    ...(effectiveOus.length > 0 && { ouId: effectiveOus.join(',') }),
    ...(location.length > 0 && { location: location.join(',') }),
    ...(effectiveSkills.length > 0 && { skillId: effectiveSkills.join(',') }),
    sortBy: effectiveSort,
  }
  const queryFn = ({ pageParam = 1, signal }: any) => fetchPractitioners({ ...params, page: pageParam })(signal)
  return useInfiniteQuery({
    queryKey: ['practitioners', params],
    queryFn,
    initialPageParam: 1,
    getNextPageParam: pageResolver,
    enabled: !urlSkillsParam || !!skillList,
    select: ({ pages, pageParams }) => ({
      pages: pages.map((page: any) => page.data.data),
      pageParams,
      exploreMorePractitioners: pages[0]?.data?.exploreMorePractitioners,
      meta: pages[0]?.data?.meta,
    }),
  })
}
